import React, { useState } from 'react';
import { Dropdown, Spinner, Button } from 'react-bootstrap';
import { useInbox, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useInbox';
import { formatTimeAgo } from '../utils/timeUtils';

function NotificationDropdown({ CustomToggle }) {
    const { data: messagesData, isLoading, error } = useInbox(1); // Get first page for dropdown
    const { data: unreadData } = useUnreadCount();
    const markAsRead = useMarkAsRead();
    const markAllAsRead = useMarkAllAsRead();
    const [isOpen, setIsOpen] = useState(false);

    // Get unread count from API
    const unreadCount = unreadData?.unread_count || 0;

    // Handle both paginated and non-paginated responses
    const messages = messagesData?.results || messagesData || [];

    const handleMarkAsRead = (messageId) => {
        markAsRead.mutate(messageId);
    };

    const handleMarkAllAsRead = () => {
        markAllAsRead.mutate();
    };

    const handleToggle = (isOpen) => {
        setIsOpen(isOpen);
        // Mark all as read when dropdown is opened
        if (isOpen && unreadCount > 0) {
            handleMarkAllAsRead();
        }
    };

    return (
        <Dropdown align="end" onToggle={handleToggle}>
            <Dropdown.Toggle
                as={CustomToggle}
                id="notifDropdown"
                data-bs-auto-close="outside"
                data-bs-toggle=""
            >
                {unreadCount > 0 && (
                    <span className="badge-notif animation-blink"></span>
                )}
                <i className="bi bi-bell-fill fs-6"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu dropdown-animation dropdown-menu-end dropdown-menu-size-md p-0 shadow-lg border-0">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="m-0">
                            Notifications
                            {unreadCount > 0 && (
                                <span className="badge bg-danger bg-opacity-10 text-danger ms-2">
                                    {unreadCount} new
                                </span>
                            )}
                        </h6>
                        {unreadCount > 0 && (
                            <Button
                                variant="link"
                                className="small p-0 text-decoration-none"
                                onClick={handleMarkAllAsRead}
                                disabled={markAllAsRead.isPending}
                            >
                                {markAllAsRead.isPending ? 'Marking...' : 'Clear all'}
                            </Button>
                        )}
                    </div>
                    <div className="card-body p-0">
                        {isLoading ? (
                            <div className="text-center p-4">
                                <Spinner animation="border" size="sm" />
                                <p className="small text-muted mt-2">Loading notifications...</p>
                            </div>
                        ) : error ? (
                            <div className="text-center p-4">
                                <p className="small text-danger">Failed to load notifications</p>
                                <p className="small text-muted">{error.message}</p>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => window.location.reload()}
                                >
                                    Retry
                                </Button>
                            </div>
                        ) : messages?.length === 0 ? (
                            <div className="text-center p-4">
                                <i className="bi bi-bell fs-3 text-muted"></i>
                                <p className="small text-muted mt-2">No notifications yet</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush list-unstyled p-2">
                                {messages?.slice(0, 5).map((message) => (
                                    <li
                                        key={message.id}
                                        className={`list-group-item list-group-item-action rounded d-flex border-0 mb-1 p-3 ${!message.read_at ? 'badge-unread' : ''
                                            }`}
                                        onClick={() => handleMarkAsRead(message.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="avatar text-center d-none d-sm-inline-block">
                                            <img
                                                className="avatar-img rounded-circle"
                                                src={`/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                alt="notification"
                                            />
                                        </div>
                                        <div className="ms-sm-3 flex-grow-1">
                                            <p className="small mb-2">
                                                <strong>{message.subject}</strong>
                                            </p>
                                            <p className="small text-muted mb-1">
                                                {message.body.length > 100
                                                    ? `${message.body.substring(0, 100)}...`
                                                    : message.body
                                                }
                                            </p>
                                            <p className="small text-muted mb-0">
                                                {formatTimeAgo(message.created_at)}
                                            </p>
                                        </div>
                                        {!message.read_at && (
                                            <div className="ms-2">
                                                <span className="badge bg-primary rounded-pill">New</span>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {messages?.length > 5 && (
                        <div className="card-footer text-center">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                href="/notifications/list/"
                            >
                                See all notifications
                            </Button>
                        </div>
                    )}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default NotificationDropdown; 